import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button, Spinner, Tabs, Tab, Table } from "react-bootstrap";
import {
  getEmailSettings,
  updateEmailSettings,
  sendTestEmailSettings,
  getEmailTemplates,
  updateEmailTemplate,
  resetEmailTemplate,
} from "../services/api";
import { setAlert } from "../store/slices/alertSlice";
import { useBrand } from "../context/BrandContext";

const EmailSettings = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { brand } = useBrand();
  const defaultFromName = brand?.productName || "Incident Reporting System";
  const [activeTab, setActiveTab] = useState("smtp");
  const [formData, setFormData] = useState({
    enabled: false,
    host: "",
    port: 587,
    secure: false,
    username: "",
    password: "",
    fromName: defaultFromName,
    fromEmail: "",
    frontendBaseUrl: "",
    signature: "",
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState(user?.email || "");

  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [escalationRoleOptions, setEscalationRoleOptions] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [draft, setDraft] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateResetting, setTemplateResetting] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getEmailSettings();
      setFormData({
        enabled: Boolean(data.enabled),
        host: data.host || "",
        port: data.port || 587,
        secure: Boolean(data.secure),
        username: data.username || "",
        password: "",
        fromName: data.fromName || defaultFromName,
        fromEmail: data.fromEmail || "",
        frontendBaseUrl: data.frontendBaseUrl || "",
        signature: data.signature || "",
      });
      setPasswordSet(Boolean(data.passwordSet));
      if (user?.email) {
        setTestTo((current) => current || user.email);
      } else if (data.fromEmail) {
        setTestTo((current) => current || data.fromEmail);
      }
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Error loading email settings" }));
    } finally {
      setLoading(false);
    }
  }, [defaultFromName, dispatch, user?.email]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const { data } = await getEmailTemplates();
      const list = data.templates || [];
      setTemplates(list);
      setPlaceholders(data.placeholders || []);
      setEscalationRoleOptions(data.escalationRoleOptions || []);
      setSelectedType((current) => {
        if (current && list.some((item) => item.type === current)) return current;
        return list[0]?.type || "";
      });
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Error loading email templates" }));
    } finally {
      setTemplatesLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (activeTab === "templates") {
      loadTemplates();
    }
  }, [activeTab, loadTemplates]);

  useEffect(() => {
    if (user?.email) {
      setTestTo((current) => current || user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    const selected = templates.find((item) => item.type === selectedType);
    if (!selected) {
      setDraft(null);
      return;
    }
    setDraft({
      enabled: Boolean(selected.enabled),
      subject: selected.subject || "",
      bodyText: selected.bodyText || "",
      escalationRoles: Array.isArray(selected.escalationRoles) ? [...selected.escalationRoles] : [],
    });
  }, [selectedType, templates]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDraftChange = (event) => {
    const { name, value, type, checked } = event.target;
    setDraft((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleEscalationRole = (role) => {
    setDraft((current) => {
      if (!current) return current;
      const roles = new Set(current.escalationRoles || []);
      if (roles.has(role)) roles.delete(role);
      else roles.add(role);
      return { ...current, escalationRoles: [...roles] };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (formData.enabled) {
      if (!formData.host.trim() || !formData.fromEmail.trim()) {
        dispatch(setAlert({
          type: "danger",
          message: "Host and From Email are required when email is enabled",
        }));
        return;
      }
      if (!passwordSet && !String(formData.password || "").trim()) {
        dispatch(setAlert({
          type: "danger",
          message: "SMTP password is required when email is enabled",
        }));
        return;
      }
    }

    const payload = {
      enabled: formData.enabled,
      host: formData.host.trim(),
      port: Number(formData.port) || 587,
      secure: Boolean(formData.secure),
      username: formData.username.trim(),
      fromName: formData.fromName.trim(),
      fromEmail: formData.fromEmail.trim(),
      frontendBaseUrl: (formData.frontendBaseUrl || "").trim(),
      signature: (formData.signature || "").trim(),
    };

    if (String(formData.password || "").trim()) {
      payload.password = formData.password.trim();
    }

    setSaving(true);
    try {
      const { data } = await updateEmailSettings(payload);
      dispatch(setAlert({ type: "success", message: data.message || "Email settings saved" }));
      setPasswordSet(Boolean(data.settings?.passwordSet));
      if (data.settings) {
        setFormData((current) => ({
          ...current,
          enabled: Boolean(data.settings.enabled),
          host: data.settings.host || "",
          port: data.settings.port || 587,
          secure: Boolean(data.settings.secure),
          username: data.settings.username || "",
          password: "",
          fromName: data.settings.fromName || defaultFromName,
          fromEmail: data.settings.fromEmail || "",
          frontendBaseUrl: data.settings.frontendBaseUrl || "",
          signature: data.settings.signature || "",
        }));
      }
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Failed to save email settings",
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!String(testTo || "").trim()) {
      dispatch(setAlert({ type: "danger", message: "Enter a recipient email for the test" }));
      return;
    }

    setTesting(true);
    try {
      const savePayload = {
        enabled: formData.enabled,
        host: formData.host.trim(),
        port: Number(formData.port) || 587,
        secure: Boolean(formData.secure),
        username: formData.username.trim(),
        fromName: formData.fromName.trim(),
        fromEmail: formData.fromEmail.trim(),
        frontendBaseUrl: (formData.frontendBaseUrl || "").trim(),
        signature: (formData.signature || "").trim(),
      };
      if (String(formData.password || "").trim()) {
        savePayload.password = formData.password.trim();
      }

      const saved = await updateEmailSettings(savePayload);
      if (saved?.data?.settings) {
        setPasswordSet(Boolean(saved.data.settings.passwordSet));
        setFormData((current) => ({
          ...current,
          ...saved.data.settings,
          password: "",
          signature: saved.data.settings.signature || "",
          frontendBaseUrl: saved.data.settings.frontendBaseUrl || "",
        }));
      }

      const { data } = await sendTestEmailSettings({
        to: testTo.trim(),
        signature: (formData.signature || "").trim(),
        frontendBaseUrl: (formData.frontendBaseUrl || "").trim(),
      });
      dispatch(setAlert({ type: "success", message: data.message || "Test email sent" }));
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Failed to send test email",
      }));
    } finally {
      setTesting(false);
    }
  };

  const handleToggleTemplateEnabled = async (type, enabled) => {
    const current = templates.find((item) => item.type === type);
    if (!current) return;

    try {
      const { data } = await updateEmailTemplate(type, {
        enabled,
        subject: current.subject,
        bodyText: current.bodyText,
        bodyHtml: current.bodyHtml || "",
        escalationRoles: current.escalationRoles || [],
      });
      setTemplates((list) =>
        list.map((item) => (item.type === type ? data.template : item))
      );
      dispatch(setAlert({
        type: "success",
        message: enabled ? "Email enabled for this event" : "Email disabled for this event",
      }));
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Failed to update template",
      }));
    }
  };

  const handleSaveTemplate = async (event) => {
    event.preventDefault();
    if (!selectedType || !draft) return;

    setTemplateSaving(true);
    try {
      const { data } = await updateEmailTemplate(selectedType, {
        enabled: Boolean(draft.enabled),
        subject: draft.subject,
        bodyText: draft.bodyText,
        escalationRoles: draft.escalationRoles || [],
      });
      setTemplates((list) =>
        list.map((item) => (item.type === selectedType ? data.template : item))
      );
      dispatch(setAlert({ type: "success", message: data.message || "Template saved" }));
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Failed to save template",
      }));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleResetTemplate = async () => {
    if (!selectedType) return;
    setTemplateResetting(true);
    try {
      const { data } = await resetEmailTemplate(selectedType);
      setTemplates((list) =>
        list.map((item) => (item.type === selectedType ? data.template : item))
      );
      dispatch(setAlert({ type: "success", message: data.message || "Template reset" }));
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Failed to reset template",
      }));
    } finally {
      setTemplateResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Email Settings</h3>
          <p>
            Configure SMTP and per-event email templates, including optional escalation recipients.
          </p>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key || "smtp")}
        className="mb-3"
      >
        <Tab eventKey="smtp" title="SMTP">
          <Form onSubmit={handleSave} className="card p-3 shadow-sm">
            <Form.Check
              type="switch"
              id="email-enabled"
              name="enabled"
              label="Enable email notifications"
              checked={formData.enabled}
              onChange={handleChange}
              className="mb-3"
            />

            <div className="row g-3">
              <div className="col-md-8">
                <Form.Group controlId="email-host">
                  <Form.Label>SMTP Host</Form.Label>
                  <Form.Control
                    name="host"
                    value={formData.host}
                    onChange={handleChange}
                    placeholder="smtp.office365.com"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group controlId="email-port">
                  <Form.Label>Port</Form.Label>
                  <Form.Control
                    type="number"
                    name="port"
                    value={formData.port}
                    onChange={handleChange}
                    placeholder="587"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="email-username">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="user@example.com"
                    autoComplete="off"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="email-password">
                  <Form.Label>
                    Password {passwordSet ? <small className="text-muted">(saved — leave blank to keep)</small> : null}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={passwordSet ? "••••••••" : "SMTP password"}
                    autoComplete="new-password"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="email-from-name">
                  <Form.Label>From Name</Form.Label>
                  <Form.Control
                    name="fromName"
                    value={formData.fromName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="email-from-email">
                  <Form.Label>From Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="fromEmail"
                    value={formData.fromEmail}
                    onChange={handleChange}
                    placeholder="noreply@example.com"
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group controlId="email-frontend-url">
                  <Form.Label>Frontend Base URL (link in emails)</Form.Label>
                  <Form.Control
                    name="frontendBaseUrl"
                    value={formData.frontendBaseUrl}
                    onChange={handleChange}
                    placeholder="http://192.168.27.14"
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group controlId="email-signature">
                  <Form.Label>Email Signature</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="signature"
                    value={formData.signature}
                    onChange={handleChange}
                    placeholder={"Regards,\nQuality Department\nRoyal Medical Center"}
                  />
                  <Form.Text muted>
                    Appended to incident notification emails and test emails.
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Check
                  type="checkbox"
                  id="email-secure"
                  name="secure"
                  label="Use TLS/SSL (secure connection)"
                  checked={formData.secure}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-4">
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </Form>

          <div className="card p-3 shadow-sm mt-3">
            <h5 className="mb-3">Send test email</h5>
            <div className="row g-2 align-items-end">
              <div className="col-md-8">
                <Form.Group controlId="email-test-to">
                  <Form.Label>Recipient</Form.Label>
                  <Form.Control
                    type="email"
                    value={testTo}
                    onChange={(event) => setTestTo(event.target.value)}
                    placeholder="you@example.com"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Button
                  type="button"
                  variant="outline-primary"
                  className="w-100"
                  onClick={handleTest}
                  disabled={testing}
                >
                  {testing ? "Sending..." : "Send Test Email"}
                </Button>
              </div>
            </div>
            <p className="text-muted small mt-2 mb-0">
              Save SMTP settings first. Test send verifies the connection even if notifications are still disabled.
            </p>
          </div>
        </Tab>

        <Tab eventKey="templates" title="Templates">
          <div className="card p-3 shadow-sm">
            <p className="text-muted mb-3">
              Customize subject and body per notification event. Optional escalation roles receive a copy
              in addition to the primary recipients. Disabling a template skips email only; in-app notifications still work.
            </p>

            {templatesLoading ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <>
                <div className="table-responsive mb-4">
                  <Table hover size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th style={{ width: 120 }}>Email on</th>
                        <th>Escalation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((item) => (
                        <tr
                          key={item.type}
                          className={selectedType === item.type ? "table-active" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedType(item.type)}
                        >
                          <td>
                            <div className="fw-semibold">{item.label}</div>
                            <small className="text-muted">{item.type}</small>
                          </td>
                          <td onClick={(event) => event.stopPropagation()}>
                            <Form.Check
                              type="switch"
                              id={`tpl-enabled-${item.type}`}
                              checked={Boolean(item.enabled)}
                              onChange={(event) =>
                                handleToggleTemplateEnabled(item.type, event.target.checked)
                              }
                              label=""
                            />
                          </td>
                          <td>
                            {(item.escalationRoles || []).length
                              ? item.escalationRoles.join(", ")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {draft && selectedType ? (
                  <Form onSubmit={handleSaveTemplate}>
                    <h5 className="mb-3">
                      Edit: {templates.find((item) => item.type === selectedType)?.label || selectedType}
                    </h5>

                    <Form.Check
                      type="switch"
                      id="template-enabled"
                      name="enabled"
                      label="Send email for this event"
                      checked={Boolean(draft.enabled)}
                      onChange={handleDraftChange}
                      className="mb-3"
                    />

                    <Form.Group className="mb-3" controlId="template-subject">
                      <Form.Label>Subject</Form.Label>
                      <Form.Control
                        name="subject"
                        value={draft.subject}
                        onChange={handleDraftChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="template-body">
                      <Form.Label>Body (plain text)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        name="bodyText"
                        value={draft.bodyText}
                        onChange={handleDraftChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Escalation roles (extra recipients)</Form.Label>
                      <div className="d-flex flex-wrap gap-3">
                        {escalationRoleOptions.map((role) => (
                          <Form.Check
                            key={role}
                            type="checkbox"
                            id={`escalation-${role}`}
                            label={role}
                            checked={(draft.escalationRoles || []).includes(role)}
                            onChange={() => toggleEscalationRole(role)}
                          />
                        ))}
                      </div>
                      <Form.Text muted>
                        Active users with these roles also receive this email (skipped if they are already a primary recipient).
                      </Form.Text>
                    </Form.Group>

                    <div className="mb-3">
                      <Form.Label className="d-block">Placeholders</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {placeholders.map((token) => (
                          <code key={token} className="px-2 py-1 bg-light border rounded">
                            {token}
                          </code>
                        ))}
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      <Button type="submit" variant="primary" disabled={templateSaving}>
                        {templateSaving ? "Saving..." : "Save Template"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={handleResetTemplate}
                        disabled={templateResetting}
                      >
                        {templateResetting ? "Resetting..." : "Reset to Default"}
                      </Button>
                    </div>
                  </Form>
                ) : null}
              </>
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default EmailSettings;
